import React, { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { setModal, selectModal } from "../../../redux/modalSlice";
import { useForm, Controller } from 'react-hook-form'
import { useRequest } from '../../../hooks/UseRequest'
import { setNotification } from '../../../redux/notificationSlice'
import { selectDropdowns } from '../../../redux/dropdownSlice'
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestionCircle } from "@fortawesome/free-solid-svg-icons";
import Dropzone from "react-dropzone";
import Loading from "../../../components/Loading";
import { useNavigate } from "react-router-dom";
import SpecialInstruction from "../../../components/elements/SpecialInstruction";
import { triggerRerender } from '../../../redux/rerenderSlice'

export default function PowerOfAttorneyModal() {
  const dispatch = useDispatch();
  const modal = useSelector(selectModal);
  const { register, reset, handleSubmit, control, watch, setValue, formState: { errors }, } = useForm({ defaultValues: { is_active: true, } });
  const [saving, setSaving] = useState(false)
  const request = useRequest()
  const customerId = modal.options?.customerId
  const [file, setFile] = useState(null);
  const dropdownOptions = useSelector(selectDropdowns);
  const [isLoading, setIsLoading] = useState(true);
  const [tabState, setTabState] = useState(0)
  const [updateMode, setUpdateMode] = useState(false);
  const [customerPOA, setCustomerPOA] = useState(null);
  const [loanPOA, setLoanPOA] = useState(null);
  const active = modal.is_active && modal.type === 'power_of_attorney_modal'
  const inFlightCustomerId = useRef(null)
  const navi = useNavigate()

  const relationshipDropdownOptions = dropdownOptions?.data?.filter((o) => o.code_key === 'poa_relationship_type')[0]?.dropdown_options

  const resetState = () => {
    reset();
    setFile(null);
    setSaving(false);
    setUpdateMode(false);
    setLoanPOA(null);
    setCustomerPOA(null);
    setTabState(0);
  };

  const handleCancel = () => {
    resetState();
    dispatch(setModal({ is_active: false }));
  };

  const setFormValues = (values) => {
    setValue('phone_number', values?.phone_number || '')
    setValue('first_name', values?.first_name || '')
    setValue('email_address', values?.email_address || '')
    setValue('last_name', values?.last_name || '')
    setValue('poa_relationship_type_dropdown_option_id', values?.poa_relationship_type_dropdown_option_id)
  }

  /**
   * Handles the form submission for updating or creating a Power Of Attorney (POA) record.
   *
   * - If the customer can have a POA, then the function updates or creates a new POA record
   *   with the given email, phone number, first name, and last name.
   * - If the customer can no longer have a POA, the function updates the POA record to reflect that.
   *
   * Upon a successful operation, it executes a success callback and shows a success notification.
   * If an error occurs, it shows an error notification.
   *
   * @param {Object} formData - Form data containing the fields for POA.
   */
  const onSubmit = async (formData) => {
    // Set the saving state to true, indicating the operation is in progress
    setSaving(true)
    let response;

    try {
      // Initialize the request payload
      let body = {}

      // If the customer can have a POA, set the appropriate fields in the payload
      if (isActiveWatch) {
        body = {
          email_address: formData.email_address,
          phone_number: formData.phone_number,
          first_name: formData.first_name,
          last_name: formData.last_name,
          poa_relationship_type_dropdown_option_id: formData.poa_relationship_type_dropdown_option_id
        };
      }
      body.is_active = formData.is_active;
      if (!updateMode) {
        request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/customer/${customerId}/power_of_attorney`)
        response = await request.post(body, file)
      }
      if (updateMode) {
        request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/customer/${customerId}/power_of_attorney/${tabState === 0 ? customerPOA.id : loanPOA.id}`)
        response = await request.modify(body, file)
      }

      // If the operation was successful, execute the success callback and reset the form
      if (response.status === 200 || response.status === 201) {
        resetState();
        modal.options.successCallback()
        const pathSegments = window.location.pathname.split('/');
        const moduleName = pathSegments[1];
        dispatch(setModal({ is_active: false }))
        dispatch(triggerRerender({ subpanelId: 'document' }))
        dispatch(setNotification({ type: 'success', body: response?.data?.message }))
        setTimeout(() => navi(`/${moduleName}/${customerId}`, { state: { reloadPage: true } }), 2000);
      } else {
        // If the operation was not successful, show an error notification
        modal.options.successCallback()
        setSaving(false)
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
      }
    } catch (error) {
      // If an exception occurs, reset the saving state and show an error notification
      setSaving(false)
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
    }
  }

  const getPoADetails = async (forCustomerId) => {
    inFlightCustomerId.current = forCustomerId
    request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/customer/${forCustomerId}/power_of_attorney_info`)
    try {
      const response = await request.get();
      // Ignore a response that a close or a newer opening has superseded.
      if (inFlightCustomerId.current !== forCustomerId) return
      if (response?.data?.data?.length > 0) {
        response.data.data.forEach((poaRecord) => {
          if (poaRecord.loan_id) {
            setLoanPOA(poaRecord)
          } else {
            setCustomerPOA(poaRecord)
            setFormValues(poaRecord)
          }
        })
      } else {
        reset();
      }
    } catch (error) {
      if (inFlightCustomerId.current !== forCustomerId) return
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
    } finally {
      if (inFlightCustomerId.current === forCustomerId) setIsLoading(false);
    }
  }
  useEffect(() => {
    if (tabState === 0 && customerPOA) {
      // reset({ ...customerPOA })
      setFormValues(customerPOA)
      setUpdateMode(true);
    } else if (tabState === 1 && loanPOA) {
      // reset({ ...loanPOA })
      setFormValues(loanPOA)
      setUpdateMode(true);
    } else if (tabState === 0 && !customerPOA) {
      setUpdateMode(false)
    }
  }, [customerPOA, tabState, loanPOA]);

  useEffect(() => {
    setFormValues(tabState === 0 ? customerPOA : loanPOA)
  }, [tabState])

  const isActiveWatch = watch('is_active');

  // This modal stays mounted in the Footer; start every opening from a clean form and drop late
  // responses, so a previous customer's PoA can't put this customer's form into update mode.
  useEffect(() => {
    if (active && customerId) {
      resetState();
      setIsLoading(true);
      getPoADetails(customerId);
    } else {
      inFlightCustomerId.current = null
    }
  }, [customerId, active])

  const buildRelationshipOptions = () => {
    if (!relationshipDropdownOptions) {
      return [];
    }
    return relationshipDropdownOptions.map((r) => {
      return { value: r.id, label: r.name }
    })
  }

  // eslint-disable-next-line no-nested-ternary
  return active ? (
    // eslint-disable-next-line no-nested-ternary
    relationshipDropdownOptions.length > 0 ? (
      <form onSubmit={handleSubmit(onSubmit)} id="power_of_attorney_modal_form">
        <div className="modal is-active">
          <div className="modal-background"></div>
          <div className="modal-card is-flex is-flex-direction-column column is-8">
            <header className="modal-card-head py-3">
              <p className="modal-card-title is-capitalized">
                Power of Attorney
              </p>
              <button className="delete" aria-label="close" onClick={handleCancel}></button>
            </header>
            <section
              className="modal-card-body modal-card-body pt-1 pb-1"
              style={{ minHeight: "110px", overflow: 'hidden' }}
            >
              <div className="tabs">
                <ul>
                  <li className={`${tabState === 0 ? 'is-active' : ''}`}><a onClick={() => { setTabState(0) }}>Customer</a></li>
                  {loanPOA?.id ? <li className={`${tabState === 1 ? 'is-active' : ''}`}><a onClick={() => { setTabState(1) }}>Loan</a></li> : ''}
                </ul>
              </div>
              {isLoading ? <Loading /> : (
                <div className="mb-0 is-flex-direction-column">
                  <div className="column">
                    <h3>Use this action when a customer states they a have a Power of Attorney (PoA).</h3>
                  </div>
                  <div className="column">
                    <h3 className="has-text-danger">{tabState == 0 ? `IMPORTANT: Use this form ONLY if this PoA is at the customer level,
                    and NOT the loan level. In other words this setup will outlive this existing loan. If this is being
                    used as part of a Bankruptcy or Debt Management Company - DO NOT USE THIS FORM, use those respective
                    Quick Actions instead.` : `IMPORTANT: Use this form ONLY if this PoA is at the loan level,
                    and NOT the customer level.` }</h3>
                  </div>
                  <div className="column">
                    <div className="columns">
                      <div className="column is-one-third pb-0">
                        <label className="has-text-grey pr-1 formLabel">
                          Relationship Type
                        </label>
                      </div>
                      <div className="column is-one-third pb-0">
                        <label className="has-text-grey pr-1 formLabel">
                          Is Active
                          <span
                            className="ml-1 lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary"
                            data-tooltip=""
                          >
                            <FontAwesomeIcon icon={faQuestionCircle} className="is-size-5 has-text-link" />
                          </span>
                        </label>
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-one-third pt-0">
                        <Controller
                          name="poa_relationship_type_dropdown_option_id"
                          control={control}
                          rules={{ required: false }}
                          render={({ field }) => (
                            <div data-testid="power-of-attorney-modal-relationship-type-select">
                              <Select
                                {...field}
                                options={buildRelationshipOptions()}
                                menuPosition="fixed"
                                isSearchable={false}
                                isDisabled={tabState === 1}
                                // Keep the id in form state, not the option object, so a seeded and a picked
                                // value are one shape. Without this an untouched field submits undefined.
                                onChange={(option) => field.onChange(option?.value ?? null)}
                                value={buildRelationshipOptions().find((i) => i.value === field.value) ?? null}
                                isClearable
                              />
                            </div>
                          )}
                        />
                      </div>
                      <div className="column is-one-third pt-0">
                        <div className="checkbox-input">
                          <Controller
                            name="is_active"
                            control={control}
                            render={({ field }) => (
                              <span className="checkbox pt-1">
                                <input
                                  {...field}
                                  disabled={!updateMode}
                                  style={{ width: "30px", height: "30px" }}
                                  type="checkbox"
                                  value={field.value}
                                  checked={field.value}
                                />
                              </span>
                            )}
                          />
                        </div>
                      </div>

                    </div>
                    <div className="mt-1">
                    </div>
                  </div>

                  <div className="column subtitle has-text-weight-bold mb-0">
                    Details
                    <span
                      className="ml-1 lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary"
                      data-tooltip="Any of these fields that are filled in will override the customer’s field of the same type throughout the system."
                    >
                      <FontAwesomeIcon icon={faQuestionCircle} className="is-size-5 has-text-link" />
                    </span>
                  </div>
                  <div className="columns pl-3">
                    <div className="column is-one-third pb-0">
                      <label className="has-text-grey formLabel">
                        Email Address
                      </label>
                    </div>
                    <div className="column is-one-third pb-0">
                      <label className="has-text-grey formLabel">
                        Phone Number
                      </label>
                    </div>
                    <div className="column is-one-third pb-0">
                      <label className="has-text-grey formLabel">
                        First Name
                      </label>
                    </div>
                  </div>
                  <div className="columns  pl-3">
                    <div className="column is-one-third pt-0">
                      <div className="control">
                        <Controller
                          name="email_address"
                          control={control}
                          render={({ field }) => (
                            <input {...field} type="email" disabled={tabState === 1} value={field.value} id="email_address" data-testid="power-of-attorney-modal-email-address" className="input is-small" />
                          )}
                        />
                      </div>
                    </div>
                    <div className="column is-one-third pt-0">
                      <div className="control">
                        <Controller
                          name="phone_number"
                          control={control}
                          render={({ field }) => (
                            <input {...field} type="tel" disabled={tabState === 1} id="phone_number" className="input is-small" />
                          )}
                        />
                      </div>
                    </div>
                    <div className="column is-one-third pt-0 mr-3">

                      <input
                        {...register(`first_name`, {
                          required: false
                        })}
                        type="text"
                        disabled={tabState === 1}
                        id="first_name"
                        className="input is-small"
                      />

                    </div>
                  </div>
                  <div className="columns pl-3">
                    <div className="column is-one-third pb-0 pt-0">
                      <label className="has-text-grey formLabel">
                        Last Name
                      </label>
                    </div>
                  </div>
                  <div className="columns pl-3">
                    <div className="column is-one-third pt-0">
                      <div className="control">
                        <Controller
                          name="last_name"
                          control={control}
                          defaultValue=""
                          render={({ field }) => (
                            <input {...field} disabled={tabState === 1} type="text" value={field.value} id="last_name" className="input is-small" />
                          )}
                        />
                      </div>
                    </div>
                  </div>
                  {tabState === 0 ? (
                    <>
                  <div className="column subtitle has-text-weight-bold mb-0">
                    Supporting Files
                  </div>
                  <div className="column pb-0">
                    <label className="has-text-grey formLabel">
                      Power of Attorney
                    </label>
                  </div>
                  <div className="column pt-0">

                    <Controller
                      name="file"
                      control={control}
                      render={(field) => (
                        <Dropzone onDrop={(file) => {
                          setFile(file[0])
                        }}
                        >
                          {({ getRootProps }) => (
                            <div>
                              <div
                                className="is-flex is-justify-content-center is-size-5 has-text-weight-bold fa-border"
                                {...getRootProps()}
                                style={{
                                  borderColor: "black",
                                  width: "300px",
                                  height: "40px",
                                  borderStyle: "dashed",
                                  backgroundColor: "rgb(235, 239, 242)"
                                }}
                              >
                                <div className="is-flex is-justify-content-center">
                                  Drop File
                                </div>
                              </div>
                              <aside>
                                <ul>
                                  <li>{file?.name}</li>
                                </ul>
                              </aside>
                            </div>
                          )}
                        </Dropzone>
                      )}
                    />
                  </div> </>
                  ) : null }
                  <div className="column">
                    <h3>
                      {(() => {
                        if (isActiveWatch && tabState === 0) return (
                          <SpecialInstruction
                            content={"<span>" +
                            "<span class='has-text-weight-bold'>This will perform the following actions:</span>" + "<br>" +
                            `<span class='pl-2'>• ${updateMode ? 'Update' : 'Create'} record in power_of_attorney_customers for customer_id</span> <br> ` +
                            "<span class='pl-2'>• Any attorney info provided will replace the customer’s for the duration of this loan</span> <br> " +
                            " </span>"}
                            type="is-warning"
                          />
                        )
                        if (isActiveWatch && tabState === 1) return null;
                        return (
                          <SpecialInstruction
                            content={"<span>" +
                              "<span class='has-text-weight-bold'>This will perform the following actions:</span>" + "<br>" +
                              `<span class='pl-2'>• Set power_of_attorney_customers record to inactive (not in force)</span> <br> ` +
                              "<span class='pl-2'>• Customer’s contact details will be used for outreach again</span> <br> " +
                              " </span>"}
                            type="is-danger"
                          />
                        )
                      })()}

                    </h3>
                  </div>
                </div>
              )}
            </section>
            <footer className="modal-card-foot is-justify-content-end py-1">
              <div className="lox-form__buttons-wrapper has-text-right">
                <button type="button" className="button is-small has-background-grey has-text-white" onClick={handleCancel}>Cancel</button>
                <button type="submit" className="button is-link is-small" data-testid="power-of-attorney-modal-save" disabled={(saving || (tabState === 1 && isActiveWatch)) && 'disabled'}>{saving ? 'Submitting' : 'Continue'}</button>
              </div>
            </footer>
          </div>
        </div>
      </form>
    ) : null
  ) : null;
}
